/**
 * České formátování čísel. UI je česky (spec 00 §7), takže desetinná čárka
 * a mezera jako oddělovač tisíců — nikdy anglický formát.
 */

const NBSP = ' ';

function cs(value: number, options: Intl.NumberFormatOptions): string {
  return new Intl.NumberFormat('cs-CZ', options).format(value);
}

/** Celé číslo s oddělovačem tisíců: 1 234 567 */
export function formatInt(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return cs(value, { maximumFractionDigits: 0 });
}

/** Desetinné číslo: 4,70 */
export function formatNumber(
  value: number | null | undefined,
  digits = 2,
): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return cs(value, { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

/** Podíl 0–1 jako procento: 0.047 → „4,70 %" */
export function formatPercent(
  value: number | null | undefined,
  digits = 2,
): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return `${cs(value * 100, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}${NBSP}%`;
}

/**
 * Absolutní rozdíl dvou měr v procentních bodech, se znaménkem.
 * Zrcadlí format_pb() z backend core/stats.py: 0.0033 → „+0,33 pb"
 */
export function formatPb(value: number | null | undefined, digits = 2): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  const formatted = cs(value * 100, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    signDisplay: 'always',
  });
  return `${formatted}${NBSP}pb`;
}

/** Relativní změna: 0.12 → „+12,0 %" */
export function formatDeltaPercent(
  value: number | null | undefined,
  digits = 1,
): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return `${cs(value * 100, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    signDisplay: 'always',
  })}${NBSP}%`;
}

/** Koruny. `null` znamená „nemáme data", ne nulu — volající to musí rozlišit. */
export function formatCzk(value: number | null | undefined, digits = 0): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return `${cs(value, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}${NBSP}Kč`;
}

/** Kompaktní zápis velkých čísel do tabulek: 1,2 mil. */
export function formatCompact(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  if (Math.abs(value) < 10_000) return formatInt(value);
  return cs(value, { notation: 'compact', maximumFractionDigits: 1 });
}

/** p-hodnota — velmi malé hodnoty vědecky, jinak 3 desetinná místa. */
export function formatPValue(p: number | null | undefined): string {
  if (p === null || p === undefined || !Number.isFinite(p)) return '—';
  if (p < 0.0001) return p.toExponential(1).replace('.', ',').replace('e', '·10^');
  return cs(p, { minimumFractionDigits: 3, maximumFractionDigits: 4 });
}

/** Velikost vzorku pro popisky „n = 12 345". */
export function formatN(n: number | null | undefined): string {
  return `n${NBSP}=${NBSP}${formatInt(n)}`;
}

/** Sekundy → „3,3 s" */
export function formatSeconds(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return `${formatNumber(value, digits)}${NBSP}s`;
}

/** Interval spolehlivosti jako „4,52–4,88 %" */
export function formatCi(
  low: number | null | undefined,
  high: number | null | undefined,
  digits = 2,
): string {
  if (low === null || low === undefined || high === null || high === undefined) return '—';
  const l = cs(low * 100, { minimumFractionDigits: digits, maximumFractionDigits: digits });
  const h = cs(high * 100, { minimumFractionDigits: digits, maximumFractionDigits: digits });
  return `${l}–${h}${NBSP}%`;
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('cs-CZ', {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('cs-CZ', {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
  }).format(d);
}

/** „před 3 h" pro indikátor čerstvosti. */
export function formatAge(hours: number | null | undefined): string {
  if (hours === null || hours === undefined || !Number.isFinite(hours)) return 'neznámo';
  if (hours < 1) return `před ${Math.round(hours * 60)}${NBSP}min`;
  if (hours < 48) return `před ${formatNumber(hours, 1)}${NBSP}h`;
  return `před ${Math.round(hours / 24)}${NBSP}dny`;
}

/**
 * Dolary — ceny AI běhů backend vede v USD (`cost_usd`, `budget_month_usd`),
 * ne v korunách. Nepřepočítáváme: kurz nikde neevidujeme a vymyšlený převod by
 * v rozpočtovém stropu lhal.
 */
export function formatUsd(value: number | null | undefined, digits = 4): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  const abs = Math.abs(value);
  const shown = abs > 0 && abs < 0.01 ? digits : 2;
  return `${cs(value, { minimumFractionDigits: shown, maximumFractionDigits: shown })}${NBSP}$`;
}
